// src/app/components/admin/wind-tunnel/wind-tunnel-page.tsx
//
// Admin tools → Wind tunnel. A pre-deployment content test: the brand's
// questions asked as control (question alone) and as one injection arm per
// draft (up to four drafts, each in front of the question) on the same models,
// judged by the platform judge, compared side by side and against do-no-harm
// gates before the content goes live.
//
// Laid out on the admin portal's shell (same as Global Settings): a 280px left
// column — header, "New test", then every test as a nav row — inline on
// desktop and an off-canvas drawer below it; the content column scrolls with
// the document.
//
// Standalone on purpose — not an onboarding stage — so a prospect, a demo
// brand and a signed client all go through one door. Not to be confused with
// Stage 5's "Pre-deployment validation", a scorer-stability check on one run.

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ChevronRight, FlaskConical, Loader2, PanelLeft, Plus, X } from "lucide-react";

import { windTunnelApi, type WindTunnelOptions, type WindTunnelRun } from "@/api/wind-tunnel";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { DraftVerdictMarks, ErrorNote, StatusText, VerdictChip, shortDate } from "./wind-tunnel-shared";
import { WindTunnelNewTest } from "./wind-tunnel-new-test";
import { WindTunnelRunDetail } from "./wind-tunnel-run-detail";

type View = { kind: "new" } | { kind: "run"; id: number };

const ACCENT = "var(--alpha-teal)";

export function WindTunnelPage() {
  const { isDesktop } = useResponsive();
  const [options, setOptions] = useState<WindTunnelOptions | null>(null);
  const [runs, setRuns] = useState<WindTunnelRun[]>([]);
  const [view, setView] = useState<View>({ kind: "new" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [err, setErr] = useState("");

  const loadRuns = useCallback(() => {
    windTunnelApi.list().then((r) => setRuns(r.runs)).catch((e) => setErr(String(e?.message || e)));
  }, []);

  useEffect(() => {
    windTunnelApi.options().then(setOptions).catch((e) => setErr(String(e?.message || e)));
    loadRuns();
  }, [loadRuns]);

  // Keep the list's status chips moving while anything is in flight.
  const anyLive = runs.some((r) => r.status === "pending" || r.status === "running");
  useEffect(() => {
    if (!anyLive) return;
    const timer = window.setInterval(loadRuns, 8000);
    return () => window.clearInterval(timer);
  }, [anyLive, loadRuns]);

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const open = (next: View) => {
    setView(next);
    setDrawerOpen(false);
  };

  const activeRun = view.kind === "run" ? runs.find((r) => r.id === view.id) : undefined;

  const navRow = (key: string, isActive: boolean, onClick: () => void, body: ReactNode) => (
    <button
      key={key}
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 24px",
        backgroundColor: isActive ? "var(--muted)" : "transparent",
        border: "none",
        borderLeft: isActive ? `3px solid ${ACCENT}` : "3px solid transparent",
        cursor: "pointer",
        transition: "all 0.2s ease",
        textAlign: "left",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
      {isActive && <ChevronRight style={{ width: 16, height: 16, color: ACCENT, flexShrink: 0 }} />}
    </button>
  );

  const navContent = (
    <>
      <div
        style={{
          height: "89px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 24px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "var(--alpha-teal-10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FlaskConical style={{ width: "16px", height: "16px", color: ACCENT }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", lineHeight: 1.2 }}>
              Wind tunnel
            </h2>
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Admin · pre-deployment tests</p>
          </div>
          {!isDesktop && (
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close tests menu"
              style={{
                marginLeft: "auto",
                padding: 6,
                borderRadius: 7,
                border: "1px solid var(--border)",
                backgroundColor: "var(--muted)",
                cursor: "pointer",
                color: "var(--muted-foreground)",
                display: "flex",
              }}
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>
      </div>

      <nav style={{ flex: 1, paddingTop: "16px", overflowY: "auto" }}>
        {navRow("new", view.kind === "new", () => open({ kind: "new" }), (
          <span className="flex items-center gap-3">
            <Plus style={{ width: 18, height: 18, color: view.kind === "new" ? ACCENT : "var(--muted-foreground)", flexShrink: 0 }} />
            <span style={{
              fontSize: "var(--text-sm)",
              fontWeight: view.kind === "new" ? "var(--font-weight-semibold)" : "var(--font-weight-medium)",
              color: view.kind === "new" ? "var(--foreground)" : "var(--muted-foreground)",
            }}>
              New test
            </span>
          </span>
        ))}

        <div style={{ padding: "16px 24px 6px", fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--muted-foreground)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Tests
        </div>
        {runs.length === 0 && (
          <p style={{ padding: "4px 24px", fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>None yet.</p>
        )}
        {runs.map((run) => {
          const isActive = view.kind === "run" && view.id === run.id;
          const gated = (run.config_json?.arms || []).includes("injection");
          const live = run.status === "running" || run.status === "pending";
          return navRow(String(run.id), isActive, () => open({ kind: "run", id: run.id }), (
            <>
              <div className="flex items-center justify-between gap-2">
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: isActive ? "var(--font-weight-semibold)" : "var(--font-weight-medium)",
                    color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {run.brand_name}
                </span>
                {gated && (run.draft_verdicts?.length ?? 0) > 1 ? (
                  <DraftVerdictMarks verdicts={run.draft_verdicts!} provisional={run.status !== "completed"} />
                ) : gated ? (
                  <VerdictChip verdict={run.verdict} provisional={run.verdict !== null && run.status !== "completed"} />
                ) : (
                  <span style={{ fontSize: 11.5, color: "var(--muted-foreground)" }}>re-check</span>
                )}
              </div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                #{run.id} · {run.name || shortDate(run.created_at)}
              </div>
              <div className="flex items-center gap-2">
                <StatusText status={run.status} stopReason={run.stop_reason} stale={run.stale} />
                {live && (
                  <span className="inline-flex items-center gap-1" style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {run.calls_completed}/{run.calls_total}
                  </span>
                )}
              </div>
            </>
          ));
        })}
      </nav>
    </>
  );

  return (
    // No min-h-screen: this renders below the SiteHeader inside a shell that
    // already is (see GlobalSettingsPage for the dead-band it caused).
    <div className="flex" style={{ backgroundColor: "var(--background)" }}>
      {isDesktop ? (
        <aside style={{ width: "280px", borderRight: "1px solid var(--border)", backgroundColor: "var(--background)", flexShrink: 0 }}>
          <div style={{ position: "sticky", top: 0, maxHeight: "100vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {navContent}
          </div>
        </aside>
      ) : (
        <>
          {drawerOpen && (
            <div
              onClick={() => setDrawerOpen(false)}
              style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 45 }}
              aria-hidden="true"
            />
          )}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100dvh",
              width: "min(320px, 85vw)",
              zIndex: 46,
              backgroundColor: "var(--background)",
              borderRight: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 300ms ease-in-out",
              boxShadow: drawerOpen ? "0 0 30px rgba(0,0,0,0.15)" : "none",
            }}
            aria-hidden={!drawerOpen}
          >
            {navContent}
          </div>
        </>
      )}

      <main className="flex-1" style={{ backgroundColor: "var(--background)", minWidth: 0, paddingBottom: 24 }}>
        {!isDesktop && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              margin: "20px 16px 0",
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted-30)",
            }}
          >
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open tests"
              title="Tests"
              style={{
                padding: 8,
                borderRadius: 8,
                border: "1px solid var(--border)",
                backgroundColor: "var(--background)",
                cursor: "pointer",
                color: "var(--foreground)",
                display: "flex",
                flexShrink: 0,
              }}
            >
              <PanelLeft style={{ width: 16, height: 16 }} />
            </button>
            <FlaskConical style={{ width: 16, height: 16, color: ACCENT, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {view.kind === "new" ? "New test" : activeRun ? `${activeRun.brand_name} #${activeRun.id}` : `Test #${view.id}`}
            </div>
          </div>
        )}

        <div className="px-4 md:px-6 py-5 md:py-6">
          {err && <div className="mb-4"><ErrorNote>{err}</ErrorNote></div>}
          {view.kind === "new" ? (
            options ? (
              <WindTunnelNewTest
                options={options}
                onLaunched={(run) => {
                  loadRuns();
                  setView({ kind: "run", id: run.id });
                }}
              />
            ) : (
              <div className="flex items-center gap-2" style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
                <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Loading…
              </div>
            )
          ) : (
            <WindTunnelRunDetail
              key={view.id}
              runId={view.id}
              onOpenRun={(id) => setView({ kind: "run", id })}
              onChanged={loadRuns}
            />
          )}
        </div>
      </main>
    </div>
  );
}
